import { type PersonalizzazioniRiepilogoDTO } from '@framework/api-client/api/inquiries-compile/schemas/personalizzazioniRiepilogoDTO.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type RichiestaModuloDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTO.ts';
import { type TipoDomandaDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoDomandaDTO.ts';
import { StyledHtmlText } from '@framework/engine/inputs/rich-text/style/StyledHtmlText.tsx';
import { Flex, Typography } from 'antd';
import DOMPurify from 'dompurify';
import { SubmissionBurdenLogic } from '../../../burden/SubmissionBurdenLogic.ts';
import { ExtendedAttachmentsTable } from './ExtendedAttachmentsTable.tsx';
import { ExtendedModulesTable } from './ExtendedModulesTable.tsx';
import { SummaryPaymentsSection } from './payments/SummaryPaymentsSection.tsx';
import { SummaryGeneralDataSection } from './SummaryGeneralDataSection.tsx';
import { SummaryInlineViewerProvider } from './SummaryInlineViewerProvider.tsx';
import { SummaryPersonalDataSection } from './SummaryPersonalDataSection.tsx';

export interface IExtendedSubmissionSummaryProps {
  text?: string | null;
  submission?: RichiestaDTO;
  modules?: RichiestaModuloDTO[];
  submissionId: string;
  inquiryType?: TipoDomandaDTO;
  customizations?: PersonalizzazioniRiepilogoDTO | null;
  /** Se true, nasconde le azioni sui pagamenti (es. consultazione dal Backoffice) */
  readonly?: boolean;
}

/**
 * Riepilogo esteso della richiesta con tabelle moduli e allegati.
 * Utilizzato quando modalitaRiepilogo === 'ESTESO'.
 */
export function ExtendedSubmissionSummary(
  props: IExtendedSubmissionSummaryProps,
) {
  const {
    text,
    submission,
    modules,
    submissionId,
    inquiryType,
    customizations,
    readonly,
  } = props;
  const showPayments = SubmissionBurdenLogic.hasBurdens(submission?.statoOneri);

  return (
    <SummaryInlineViewerProvider>
      {text && (
        <Typography.Paragraph>
          <StyledHtmlText
            // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitize is safe
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(text),
            }}
          />
        </Typography.Paragraph>
      )}
      <Flex vertical gap={24}>
        <SummaryGeneralDataSection
          submission={submission}
          inquiryType={inquiryType}
          customizations={customizations}
        />
        <SummaryPersonalDataSection
          submission={submission}
          submissionId={submissionId}
          customizations={customizations}
        />
        {modules && modules.length > 0 && (
          <ExtendedModulesTable
            submissionId={submissionId}
            modules={modules}
            submission={submission}
          />
        )}
        <ExtendedAttachmentsTable
          submissionId={submissionId}
          modules={modules ?? []}
          submission={submission}
        />
        {showPayments && submission && (
          <SummaryPaymentsSection submission={submission} readonly={readonly} />
        )}
      </Flex>
    </SummaryInlineViewerProvider>
  );
}
